import { useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

const groupedCategories = [
  {
    group: 'Clothing',
    items: categories.filter((c) =>
      ['Shoes', 'Hoodies/Sweaters', 'T-Shirts', 'Jackets', 'Pants/Shorts', 'Jersey', 'Sets', 'Underwear/Underpants'].includes(c.name)
    ),
  },
  {
    group: 'Accessories & More',
    items: categories.filter((c) =>
      ['Headwear', 'Accessories', 'Bags', 'Belts', 'Perfumes', 'Other Stuff'].includes(c.name)
    ),
  },
];

const guideLinks = [
  { label: 'Complete Guide', path: '/sugargoo-spreadsheet-guide' },
  { label: 'Shipping Cost', path: '/sugargoo-shipping-cost' },
  { label: 'Coupons & Deals', path: '/sugargoo-coupon' },
  { label: 'Warehouse Guide', path: '/sugargoo-warehouse' },
  { label: 'Beginner Tips', path: '/sugargoo-beginner-tips' },
  { label: 'Is Sugargoo Legit?', path: '/is-sugargoo-legit' },
  { label: 'Platform Review', path: '/sugargoo-review' },
  { label: 'Best Finds', path: '/sugargoo-spreadsheet-finds' },
  { label: 'Shopping Strategy', path: '/sugargoo-shopping-strategy' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              Sugargoo<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap font-medium"
            >
              Home
            </Link>

            <div className="relative">
              <button
                className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap font-medium"
                onClick={() => setGuideOpen(!guideOpen)}
                onBlur={() => setTimeout(() => setGuideOpen(false), 200)}
              >
                Shopping Guide
                <i className={`${guideOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-4 h-4 flex items-center justify-center transition-transform`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/80 shadow-lg py-2 z-50">
                  {guideLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors whitespace-nowrap"
                      onClick={() => setGuideOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {['Shoes', 'Hoodies/Sweaters', 'T-Shirts', 'Jackets', 'Accessories'].map((cat) => {
              const found = categories.find((c) => c.name === cat);
              return found ? (
                <a
                  key={cat}
                  href={found.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
                >
                  {cat}
                </a>
              ) : null;
            })}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap px-3 py-1.5 rounded-full border border-background-300 hover:border-primary-400 cursor-pointer"
            >
              Shop Now
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap px-4 py-1.5 rounded-full cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-4 px-2 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                Home
              </Link>

              <div className="border-t border-background-200 my-2"></div>

              <div className="px-3 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Shopping Guides
              </div>
              {guideLinks.slice(0, 5).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <i className="ri-article-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                  {link.label}
                </Link>
              ))}

              <div className="border-t border-background-200 my-2"></div>

              <div className="px-3 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                More Guides
              </div>
              {guideLinks.slice(5).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <i className="ri-article-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                  {link.label}
                </Link>
              ))}

              <div className="border-t border-background-200 my-2"></div>

              {groupedCategories.map((group) => (
                <div key={group.group}>
                  <div className="px-3 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    {group.group}
                  </div>
                  {group.items.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      <i className={`${cat.icon} w-5 h-5 flex items-center justify-center text-foreground-400`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              ))}

              <div className="border-t border-background-200 pt-3 mt-2 flex flex-col gap-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 text-center text-sm bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap px-4 py-2.5 rounded-full"
                >
                  <i className="ri-shopping-cart-line w-4 h-4 flex items-center justify-center"></i>
                  Shop Now
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 text-center text-sm border border-primary-400 text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap px-4 py-2.5 rounded-full"
                >
                  <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}